import React, { useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatInrAmount } from "../../../utils/reportFormat";
import { getFuelColor } from "../../../utils/fuelLabelColor";

const FILTER_OPTIONS = [
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const seriesLabel = (series) => {
  if (!series) {
    return "";
  }
  const typeLabel = series.type === "purchase" ? "Purchase" : "Sales";
  return `${series.fuel} · ${typeLabel}`;
};

const seriesStroke = (series) => {
  const base = getFuelColor(series?.fuel);
  if (series?.type === "purchase") {
    return base;
  }
  return base;
};

const formatPrice = (value) =>
  formatInrAmount(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatAxisPrice = (value) => {
  const num = Number(value || 0);
  if (num >= 1000) {
    return `₹${(num / 1000).toFixed(1)}k`;
  }
  return `₹${Math.round(num)}`;
};

const PriceTrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const rows = [...payload]
    .filter((item) => item.value != null && item.value !== "")
    .sort((a, b) => toNumber(b.value) - toNumber(a.value));

  return (
    <div className="emp-price-trend__tooltip">
      <p className="emp-price-trend__tooltip-title">{label}</p>
      {rows.map((item) => (
        <div className="emp-price-trend__tooltip-row" key={item.dataKey}>
          <span
            className="emp-price-trend__tooltip-dot"
            style={{
              backgroundColor: item.color,
              opacity: item.payload?.strokeDasharray ? 0.75 : 1,
            }}
          />
          <span className="emp-price-trend__tooltip-label">{item.name}</span>
          <span className="emp-price-trend__tooltip-value">{formatPrice(item.value)}</span>
        </div>
      ))}
    </div>
  );
};

const LegendContent = ({ payload }) => {
  if (!payload?.length) {
    return null;
  }

  return (
    <div className="emp-price-trend__legend">
      {payload.map((entry) => {
        const isPurchase = String(entry.value || "").includes("Purchase");
        return (
          <span className="emp-price-trend__legend-item" key={entry.value}>
            <span
              className={`emp-price-trend__legend-line${isPurchase ? " emp-price-trend__legend-line--dashed" : ""}`}
              style={{ borderColor: entry.color }}
            />
            {entry.value}
          </span>
        );
      })}
    </div>
  );
};

const EmployeeFuelPriceTrendChart = ({
  data = [],
  series = [],
  filter = "daily",
  onFilterChange,
  loading = false,
}) => {
  const chartSeries = useMemo(
    () => (series || []).filter((item) => item?.key),
    [series],
  );

  const summary = useMemo(() => {
    const salesKeys = chartSeries.filter((s) => s.type === "sales").map((s) => s.key);
    const purchaseKeys = chartSeries.filter((s) => s.type === "purchase").map((s) => s.key);

    let salesSum = 0;
    let salesCount = 0;
    let purchaseSum = 0;
    let purchaseCount = 0;

    (data || []).forEach((point) => {
      salesKeys.forEach((key) => {
        const value = toNumber(point[key]);
        if (value > 0) {
          salesSum += value;
          salesCount += 1;
        }
      });
      purchaseKeys.forEach((key) => {
        const value = toNumber(point[key]);
        if (value > 0) {
          purchaseSum += value;
          purchaseCount += 1;
        }
      });
    });

    const avgSales = salesCount > 0 ? salesSum / salesCount : 0;
    const avgPurchase = purchaseCount > 0 ? purchaseSum / purchaseCount : 0;

    return {
      avgSales,
      avgPurchase,
      spread: avgSales - avgPurchase,
      seriesCount: chartSeries.length,
    };
  }, [chartSeries, data]);

  if (loading) {
    return (
      <div className="emp-price-trend__empty">
        <div className="spinner-border text-primary spinner-border-sm" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <p>Loading price trend…</p>
      </div>
    );
  }

  if (!data.length || !chartSeries.length) {
    return (
      <div className="emp-price-trend">
        <div className="emp-price-trend__toolbar">
          <div className="emp-dashboard__filter-pills">
            {FILTER_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`btn btn-sm ${filter === option.key ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => onFilterChange?.(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <div className="emp-price-trend__empty">
          <i className="fa fa-area-chart emp-price-trend__empty-icon" aria-hidden="true" />
          <p>No price history for this period.</p>
          <small>Sales prices update from product price history; purchase prices from fuel unloading.</small>
        </div>
      </div>
    );
  }

  return (
    <div className="emp-price-trend">
      <div className="emp-price-trend__toolbar">
        <div className="emp-price-trend__kpis">
          <div className="emp-price-trend__kpi emp-price-trend__kpi--green">
            <span>Avg sales / L</span>
            <strong>{formatPrice(summary.avgSales)}</strong>
          </div>
          <div className="emp-price-trend__kpi emp-price-trend__kpi--orange">
            <span>Avg purchase / L</span>
            <strong>{formatPrice(summary.avgPurchase)}</strong>
          </div>
          <div className="emp-price-trend__kpi emp-price-trend__kpi--brand">
            <span>Avg spread / L</span>
            <strong>{formatPrice(summary.spread)}</strong>
          </div>
          <div className="emp-price-trend__kpi emp-price-trend__kpi--slate">
            <span>Price lines</span>
            <strong>{summary.seriesCount}</strong>
          </div>
        </div>
        <div className="emp-dashboard__filter-pills">
          {FILTER_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={`btn btn-sm ${filter === option.key ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => onFilterChange?.(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="emp-price-trend__chart">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            <CartesianGrid stroke="#e8edf3" strokeDasharray="4 4" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fill: "#64748b", fontSize: 11 }}
              axisLine={{ stroke: "#e2e8f0" }}
              tickLine={false}
              dy={6}
              interval="preserveStartEnd"
              minTickGap={28}
            />
            <YAxis
              tickFormatter={formatAxisPrice}
              tick={{ fill: "#64748b", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={56}
              domain={["auto", "auto"]}
            />
            <Tooltip content={<PriceTrendTooltip />} />
            <Legend content={<LegendContent />} />

            {chartSeries.map((item) => {
              const color = seriesStroke(item);
              const isPurchase = item.type === "purchase";
              return (
                <Line
                  key={item.key}
                  type="monotone"
                  dataKey={item.key}
                  name={seriesLabel(item)}
                  stroke={color}
                  strokeWidth={isPurchase ? 2 : 2.5}
                  strokeDasharray={isPurchase ? "8 5" : "0"}
                  dot={false}
                  activeDot={{ r: 5, strokeWidth: 2, fill: "#fff", stroke: color }}
                  connectNulls
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <p className="emp-price-trend__footnote">
        Solid lines = sales price · dashed lines = purchase price (₹ per litre)
      </p>
    </div>
  );
};

export default EmployeeFuelPriceTrendChart;
